import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import Register from './pages/Register';
import StaffRegister from './pages/StaffRegister';
import StaffReview from './pages/StaffReview';
import TaskList from './pages/TaskList';
import AddTask from './pages/AddTask';
import TaskDetail from './pages/TaskDetail';
import ReviewTaskDetail from './pages/ReviewTaskDetail';
import UserManage from './pages/UserManage';
import InspectList from './pages/InspectList';
import AddInspection from './pages/AddInspection';
import SupplyManage from './pages/SupplyManage';
import Report from './pages/Report';
import DailyTask from './pages/DailyTask';
import ChecklistAdmin from './pages/ChecklistAdmin';
import ChecklistExecute from './pages/ChecklistExecute';
import WaterMeter from './pages/WaterMeter';
import ParcelRegister from './pages/ParcelRegister';
import Dashboard from './pages/Dashboard';
import OwnerHome from './pages/OwnerHome';
import OwnerWater from './pages/OwnerWater';
import OwnerSubmit from './pages/OwnerSubmit';
import OwnerRecords from './pages/OwnerRecords';
import OwnerProfile from './pages/OwnerProfile';
import OwnerFace from './pages/OwnerFace';
import OwnerProperty from './pages/OwnerProperty';
import PropertyArchive from './pages/PropertyArchive';
import OwnerParking from './pages/OwnerParking';
import ParkingManage from './pages/ParkingManage';
import FaceManage from './pages/FaceManage';
import Workbench from './pages/Workbench';
import InfoPage from './pages/InfoPage';
import FeeManage from './pages/FeeManage';
import OwnerFees from './pages/OwnerFees';
import CommunityManage from './pages/CommunityManage';
import OwnerCommunity from './pages/OwnerCommunity';
import OwnerVisitor from './pages/OwnerVisitor';
import VisitorRegister from './pages/VisitorRegister';
import DeliveryRegister from './pages/DeliveryRegister';
import DeliveryReview from './pages/DeliveryReview';
import OwnerEntertainment from './pages/OwnerEntertainment';
import ChineseChess from './pages/ChineseChess';
import ChineseChessOnline from './pages/ChineseChessOnline';
import InternationalChess from './pages/InternationalChess';

function NavBar({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isManager = ['admin','manager','general_manager','steward_chief'].includes(user.role);

  const tabs = [
    { path: '/', icon: '🏠', label: '工作台' },
    ...(['admin','general_manager','manager','steward_chief','steward','finance'].includes(user.role) ? [{ path: '/property-archive', icon: '🏢', label: '房屋' }] : []),
    { path: '/info', icon: 'ℹ️', label: '信息' },
    ...(isManager || user.role === 'steward' ? [{ path: '/daily', icon: '✅', label: '日常' }] : []),
    { path: '/supply', icon: '📦', label: '物品' },
    { path: '/water', icon: '💧', label: '水表' },
    ...(isManager || user.report_access ? [{ path: '/report', icon: '📊', label: '报表' }] : []),
    ...(user.role === 'admin' ? [{ path: '/users', icon: '👥', label: '账号' }] : []),

  ];

  return (
    <div style={styles.navbar}>
      {tabs.map(t => (
        <button key={t.path}
          style={{...styles.navBtn, ...(location.pathname===t.path ? styles.navBtnActive : {})}}
          onClick={() => navigate(t.path)}>
          <span style={styles.navIcon}>{t.icon}</span>
          <span style={styles.navLabel}>{t.label}</span>
        </button>
      ))}
      <button style={{...styles.navBtn, color:'#ff6b6b'}} onClick={onLogout}>
        <span style={styles.navIcon}>🚪</span>
        <span style={styles.navLabel}>退出</span>
      </button>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (saved && token) setUser(JSON.parse(saved));
  }, []);

  const handleLogin = (u) => setUser(u);
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // 访客及配送登记页为公开页面，不要求登录物业系统
  if (window.location.pathname.startsWith('/visit/') || window.location.pathname.startsWith('/delivery/')) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/visit/:token" element={<VisitorRegister />} />
          <Route path="/delivery/:token" element={<DeliveryRegister />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // 未登录：登录 / 业主注册
  if (!user) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/register" element={<Register />} />
          <Route path="/staff-register" element={<StaffRegister />} />
          <Route path="*" element={<Login onLogin={handleLogin} />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // 大屏看板不显示底部导航
  if (window.location.pathname === '/dashboard') {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // 业主专属简化界面
  if (user.role === 'owner') {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<OwnerHome user={user} onLogout={handleLogout} />} />
          <Route path="/owner/repair" element={<OwnerSubmit user={user} type="repair" />} />
          <Route path="/owner/water" element={<OwnerWater user={user} />} />
          <Route path="/owner/fees" element={<OwnerFees />} />
          <Route path="/owner/community" element={<OwnerCommunity />} />
          <Route path="/owner/complaint" element={<OwnerSubmit user={user} type="complaint" />} />
          <Route path="/owner/records" element={<OwnerRecords user={user} />} />
          <Route path="/owner/profile" element={<Navigate to="/owner/property" />} />
          <Route path="/owner/face" element={<OwnerFace user={user} />} />
          <Route path="/owner/property" element={<OwnerProperty user={user} />} />
          <Route path="/owner/parking" element={<Navigate to="/owner/property" />} />
          <Route path="/owner/visitor" element={<OwnerVisitor user={user} />} />
          <Route path="/owner/entertainment" element={<OwnerEntertainment />} />
          <Route path="/owner/chess" element={<ChineseChess />} />
          <Route path="/owner/chess/online" element={<ChineseChessOnline />} />
          <Route path="/owner/international-chess" element={<InternationalChess />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <div style={styles.app}>
        <Routes>
          <Route path="/" element={<Workbench user={user} />} />
          <Route path="/chess" element={<ChineseChess />} />
          <Route path="/entertainment" element={<OwnerEntertainment />} />
          <Route path="/international-chess" element={<InternationalChess />} />
          <Route path="/tasks" element={<TaskList user={user} />} />
          <Route path="/info" element={<InfoPage user={user} />} />
          <Route path="/add" element={<AddTask user={user} />} />
          <Route path="/task/:id" element={<TaskDetail user={user} />} />
          <Route path="/review-task/:requestId" element={<ReviewTaskDetail />} />
          <Route path="/inspect" element={<InspectList user={user} />} />
          <Route path="/inspect/add" element={<AddInspection user={user} />} />
          <Route path="/supply" element={<SupplyManage user={user} />} />
          <Route path="/report" element={<Report user={user} />} />
          <Route path="/daily" element={<DailyTask user={user} />} />
          <Route path="/checklist/admin" element={<ChecklistAdmin user={user} />} />
          <Route path="/checklist/execute/:taskId" element={<ChecklistExecute user={user} />} />
          <Route path="/water" element={<WaterMeter user={user} />} />
          <Route path="/parcels" element={['admin','general_manager','manager','steward_chief','finance','steward','engineer','security','cleaner','fire_control','order_staff'].includes(user.role) ? <ParcelRegister user={user} /> : <Navigate to="/" />} />
          <Route path="/fees" element={['admin','finance','manager','general_manager','steward','steward_chief'].includes(user.role) ? <FeeManage user={user} /> : <Navigate to="/" />} />
          <Route path="/community" element={['admin','general_manager','manager','steward_chief','steward'].includes(user.role) ? <CommunityManage user={user} /> : <Navigate to="/" />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/property-archive" element={['admin','general_manager','manager','steward_chief','steward','finance'].includes(user.role) ? <PropertyArchive user={user} /> : <Navigate to="/" />} />
          <Route path="/property-archive/:id" element={['admin','general_manager','manager','steward_chief','steward','finance'].includes(user.role) ? <PropertyArchive user={user} /> : <Navigate to="/" />} />
          <Route path="/users" element={user.role === 'admin' ? <UserManage /> : <Navigate to="/" />} />
          <Route path="/staff-review" element={user.role === 'admin' ? <StaffReview /> : <Navigate to="/" />} />
          <Route path="/delivery-review" element={['admin','general_manager','manager','steward_chief','fire_control','order_staff'].includes(user.role) ? <DeliveryReview /> : <Navigate to="/" />} />
          <Route path="/parking-manage" element={['admin','general_manager','manager','steward_chief','steward','security','fire_control','order_staff'].includes(user.role) ? <ParkingManage user={user} /> : <Navigate to="/" />} />
          <Route path="/face-manage" element={['admin','general_manager','manager','steward','engineer','finance','fire_control','order_staff'].includes(user.role) ? <FaceManage /> : <Navigate to="/" />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        <NavBar user={user} onLogout={handleLogout} />
      </div>
    </BrowserRouter>
  );
}

const styles = {
  app: { paddingBottom: '65px' },
  navbar: {
    position: 'fixed', bottom: 0, left: 0, right: 0,
    background: '#fff', borderTop: '1px solid #e0e0e0',
    display: 'flex', justifyContent: 'space-around',
    padding: '6px 0', zIndex: 100,
    boxShadow: '0 -2px 8px rgba(0,0,0,0.06)'
  },
  navBtn: { background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', padding: '4px 6px', color: '#888' },
  navBtnActive: { color: '#1a4a8a' },
  navIcon: { fontSize: '18px' },
  navLabel: { fontSize: '10px', fontWeight: 600 }
};

export default App;
